import {
    Component,
    Mesh,
    MeshAttribute,
    MeshAttributeAccessor,
    WonderlandEngine,
} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {vec3} from 'gl-matrix';

/**
 * surface-sampler
 */
export class SurfaceSampler extends Component {
    static TypeName = 'surface-sampler';

    /* Properties that are configurable in the editor */

    @property.mesh()
    mesh!: Mesh;

    triangleCount = -1;
    cumulativeWeights!: Float32Array;

    positionAttribute!: MeshAttributeAccessor<Float32ArrayConstructor>;
    positions!: Float32Array;
    normalAttribute!: MeshAttributeAccessor<Float32ArrayConstructor>;
    normals!: Float32Array;

    /* Temp variables */
    tmpA = vec3.create();
    tmpB = vec3.create();
    tmpC = vec3.create();
    vecAB = vec3.create();
    vecAC = vec3.create();
    cross = vec3.create();

    static onRegister(engine: WonderlandEngine) {
        /* Triggered when this component class is registered.
         * You can for instance register extra component types here
         * that your component may create. */
    }

    init() {
        console.log('SurfaceSampler init() with param', this.mesh);
        const indexData = this.mesh.indexData;
        this.positionAttribute = this.mesh.attribute(MeshAttribute.Position);
        this.normalAttribute = this.mesh.attribute(MeshAttribute.Normal);

        const posA = this.tmpA;
        const posB = this.tmpB;
        const posC = this.tmpC;
        const vecAB = this.vecAB;
        const vecAC = this.vecAC;
        const cross = this.cross;

        const vertexCount = this.mesh.vertexCount;
        this.positions = this.positionAttribute.createArray(vertexCount);
        this.positionAttribute.get(0, this.positions);
        this.normals = this.normalAttribute.createArray(vertexCount);
        this.normalAttribute.get(0, this.normals);

        const indexCount = indexData ? indexData.length : vertexCount;
        this.triangleCount = indexCount / 3;
        this.cumulativeWeights = new Float32Array(this.triangleCount + 1);

        let cumulativeWeight = 0;
        for (let t = 0; t < this.triangleCount; ++t) {
            this.cumulativeWeights[t] = cumulativeWeight;
            this.getTriangleAttributes(t, this.positions, posA, posB, posC);

            vec3.sub(vecAB, posB, posA);
            vec3.sub(vecAC, posC, posA);
            vec3.cross(cross, vecAB, vecAC);
            const area = 0.5 * vec3.length(cross);
            cumulativeWeight += area;
        }
        this.cumulativeWeights[this.triangleCount] = cumulativeWeight;
    }

    getTriangleAttributes(
        triangleIndex: number,
        attribute: Float32Array,
        outA: vec3,
        outB: vec3,
        outC: vec3
    ) {
        const t = triangleIndex;
        const indexData = this.mesh.indexData;
        const indexA = indexData ? indexData[t * 3] : t * 3;
        const indexB = indexData ? indexData[t * 3 + 1] : t * 3 + 1;
        const indexC = indexData ? indexData[t * 3 + 2] : t * 3 + 2;

        vec3.set(
            outA,
            attribute[indexA * 3],
            attribute[indexA * 3 + 1],
            attribute[indexA * 3 + 2]
        );
        vec3.set(
            outB,
            attribute[indexB * 3],
            attribute[indexB * 3 + 1],
            attribute[indexB * 3 + 2]
        );
        vec3.set(
            outC,
            attribute[indexC * 3],
            attribute[indexC * 3 + 1],
            attribute[indexC * 3 + 2]
        );
    }

    sample(positionOut: vec3, normalOut?: vec3) {
        /* Generate random number between [0, cumulativeWeight] */
        const weights = this.cumulativeWeights;
        const randomSample = Math.random() * weights[this.triangleCount];

        /* Binary search to which triangle it belongs */
        let min = 0;
        let max = this.triangleCount;
        let curr = ~~this.triangleCount / 2;
        while (randomSample < weights[curr] || randomSample >= weights[curr + 1]) {
            if (randomSample < weights[curr]) {
                /* Triangle is lower than current */
                max = curr;
            } else {
                /* Triangle is higher than current */
                min = curr;
            }
            curr = min + ~~((max - min) / 2);
        }

        /* Random sample point on this triangle */
        let p = Math.random();
        let q = Math.random();
        if (p + q > 1) {
            p = 1 - p;
            q = 1 - q;
        }

        /* Get positions of the point */
        this.getTriangleAttributes(curr, this.positions, this.tmpA, this.tmpB, this.tmpC);
        vec3.set(positionOut, 0, 0, 0);
        vec3.scaleAndAdd(positionOut, positionOut, this.tmpA, p);
        vec3.scaleAndAdd(positionOut, positionOut, this.tmpB, q);
        vec3.scaleAndAdd(positionOut, positionOut, this.tmpC, 1 - (p + q));

        /* Get normal of the point */
        if (normalOut) {
            this.getTriangleAttributes(curr, this.normals, this.tmpA, this.tmpB, this.tmpC);
            vec3.set(normalOut, 0, 0, 0);
            vec3.scaleAndAdd(normalOut, normalOut, this.tmpA, p);
            vec3.scaleAndAdd(normalOut, normalOut, this.tmpB, q);
            vec3.scaleAndAdd(normalOut, normalOut, this.tmpC, 1 - (p + q));
            vec3.normalize(normalOut, normalOut);
        }
    }

    start() {
        console.log('start() with param', this.mesh);
    }

    update(dt: number) {
        /* Called every frame. */
    }
}
